import React, { useState, useEffect } from 'react';
import { getDaysShort } from '../utils/dateUtils';

// Updated time slots with half-hour increments
const getHalfHourTimeSlots = () => [
  '8:00 AM', '8:30 AM', '9:00 AM', '9:30 AM', '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM', '12:00 PM', '12:30 PM', '1:00 PM', '1:30 PM',
  '2:00 PM', '2:30 PM', '3:00 PM', '3:30 PM', '4:00 PM', '4:30 PM',
  '5:00 PM', '5:30 PM', '6:00 PM', '6:30 PM', '7:00 PM', '7:30 PM', '8:00 PM', '8:30 PM', '9:00 PM'
];

// Group time slots by hour for display
const getHourGroups = () => {
  const timeSlots = getHalfHourTimeSlots();
  const hourGroups = [];
  
  for (let i = 0; i < timeSlots.length; i += 2) {
    const hourSlot = timeSlots[i];
    const halfHourSlot = timeSlots[i + 1];
    
    hourGroups.push({
      hourLabel: hourSlot, // "8:00 AM"
      slots: [
        { time: hourSlot, isHalfHour: false },
        halfHourSlot ? { time: halfHourSlot, isHalfHour: true } : null
      ].filter(Boolean)
    });
  }
  
  return hourGroups;
};

/**
 * Grid component for selecting availability time slots with half-hour increments
 */
const AvailabilityGrid = ({ 
  availability = {}, 
  onChange,
  classType = 'class',
  heatmapData = {},
  showHeatmap = false
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragValue, setDragValue] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedDay, setExpandedDay] = useState('Monday'); // First day expanded by default
  
  // Get time slots and days
  const timeSlots = getHalfHourTimeSlots();
  const hourGroups = getHourGroups();
  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const daysShort = getDaysShort();
  
  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Handle mouse leave and mouse up (end dragging)
  const handleDragEnd = () => {
    setIsDragging(false);
  };
  
  // Add event listeners for drag end outside grid
  useEffect(() => {
    if (isDragging && !isMobile) {
      window.addEventListener('mouseup', handleDragEnd);
      return () => {
        window.removeEventListener('mouseup', handleDragEnd);
      };
    }
  }, [isDragging, isMobile]);
  
  // Handle mouse down to start dragging
  const handleMouseDown = (day, time, currentValue) => {
    const key = `${day}-${time}`;
    const newValue = !currentValue;
    
    if (!isMobile) {
      setIsDragging(true);
      setDragValue(newValue);
    }
    
    // Update availability
    onChange({
      ...availability,
      [key]: newValue
    });
  };
  
  // Handle mouse enter during dragging
  const handleMouseEnter = (day, time) => {
    if (!isDragging || isMobile) return;
    
    const key = `${day}-${time}`;
    
    // Update availability with drag value
    onChange({
      ...availability,
      [key]: dragValue
    });
  };

  // Get heatmap intensity for a time slot
  const getHeatmapIntensity = (day, time) => {
    if (!showHeatmap || !heatmapData) return 0;
    const key = `${day}-${time}`;
    return heatmapData[key] || 0;
  };

  // Get heatmap tooltip text
  const getHeatmapTooltip = (day, time) => {
    const count = getHeatmapIntensity(day, time);
    if (count === 0) return null;
    if (count === 1) return "1 person looking for classmates at this time";
    return `${count} people looking for classmates at this time`;
  };

  // Get heatmap background class
  const getHeatmapClass = (count) => {
    if (count === 0) return '';
    if (count === 1) return 'bg-blue-500 bg-opacity-10';
    if (count <= 3) return 'bg-blue-500 bg-opacity-20';
    if (count <= 5) return 'bg-blue-500 bg-opacity-30';
    return 'bg-blue-500 bg-opacity-40';
  };

  // Get selection count for a day
  const getSelectionCount = (day) => {
    return timeSlots.filter(time => availability[`${day}-${time}`]).length;
  };

  // Toggle day expansion
  const toggleDay = (day) => {
    setExpandedDay(expandedDay === day ? null : day);
  };

  // Quick select time periods
  const quickSelectPeriods = [
    { label: 'All Days Morning', subLabel: '8am-12pm', startTime: '8:00 AM', endTime: '12:00 PM' },
    { label: 'All Days Lunch', subLabel: '12-2pm', startTime: '12:00 PM', endTime: '2:00 PM' },
    { label: 'All Days Afternoon', subLabel: '2-5pm', startTime: '2:00 PM', endTime: '5:00 PM' },
    { label: 'All Days Evening', subLabel: '5-9pm', startTime: '5:00 PM', endTime: '9:00 PM' }
  ];

  // Handle quick select
  const handleQuickSelect = (startTime, endTime) => {
    const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const newAvailability = { ...availability };

    // Find the index range of time slots
    const startIndex = timeSlots.indexOf(startTime);
    const endIndex = timeSlots.indexOf(endTime);

    if (startIndex === -1 || endIndex === -1) return;

    // Check if all slots in this time range are already selected
    let allSelected = true;
    weekdays.forEach(day => {
      for (let i = startIndex; i < endIndex; i++) {
        const key = `${day}-${timeSlots[i]}`;
        if (!availability[key]) {
          allSelected = false;
          break;
        }
      }
    });

    // Toggle: if all selected, deselect them; otherwise select them
    weekdays.forEach(day => {
      for (let i = startIndex; i < endIndex; i++) {
        const key = `${day}-${timeSlots[i]}`;
        newAvailability[key] = !allSelected;
      }
    });

    onChange(newAvailability);
  };

  // Quick Select Buttons Component
  const QuickSelectButtons = () => (
    <div className="mb-6">
      <div className="text-sm text-gray-400 mb-3">Quick Select:</div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {quickSelectPeriods.map((period) => (
          <button
            key={period.label}
            type="button"
            onClick={() => handleQuickSelect(period.startTime, period.endTime)}
            className="px-4 py-3 rounded-lg border border-gray-700 bg-gray-900 bg-opacity-20 text-gray-300 hover:bg-gray-800 hover:bg-opacity-40 hover:border-gray-600 transition-all duration-200 text-left"
          >
            <div className="font-medium text-sm">{period.label}</div>
            <div className="text-xs text-gray-500 mt-0.5">{period.subLabel}</div>
          </button>
        ))}
      </div>
    </div>
  );

  // Helper component for individual time slot cells
  const TimeSlotCell = ({ day, time, isSelected, onMouseDown, onMouseEnter }) => {
    const heatmapCount = getHeatmapIntensity(day, time);
    const heatmapClass = getHeatmapClass(heatmapCount);
    const tooltip = getHeatmapTooltip(day, time);
    
    return (
      <div
        className={`w-full h-10 sm:h-12 cursor-pointer flex items-center justify-center rounded-md transition-all duration-200 relative
          ${isSelected 
            ? 'bg-[#7e55f6] hover:bg-[#6a46d1]' 
            : `${heatmapClass} hover:bg-[rgba(255,255,255,0.1)]`
          }`
        }
        onMouseDown={() => onMouseDown(day, time, isSelected)}
        onMouseEnter={() => onMouseEnter(day, time)}
        title={tooltip || `${day} at ${time}`}
      >
        {isSelected && (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4 text-white" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        )}
        {/* Small indicator for heatmap data when not selected */}
        {!isSelected && heatmapCount > 0 && (
          <div className="absolute bottom-1 right-1 w-1 h-1 bg-blue-400 rounded-full"></div>
        )}
      </div>
    );
  };

  // Mobile view - accordion style
  if (isMobile) {
    return (
      <div className="space-y-4">
        <QuickSelectButtons />
        
        <div className="space-y-2">
          {days.map((day) => {
            const selectionCount = getSelectionCount(day);
            const isExpanded = expandedDay === day;
            
            return (
              <div key={day} className="bg-gray-900 bg-opacity-40 rounded-lg overflow-hidden">
                {/* Day header - clickable */}
                <button
                  onClick={() => toggleDay(day)}
                  className="w-full p-4 flex items-center justify-between text-left hover:bg-gray-800 hover:bg-opacity-50 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <svg 
                      className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'transform rotate-90' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                    <span className="font-medium text-white">{day}</span>
                  </div>
                  <span className="text-sm text-gray-400">
                    {selectionCount === 0 ? 'No times selected' : `${selectionCount} selected`}
                  </span>
                </button>
                
                {/* Day content - collapsible */}
                <div 
                  className={`transition-all duration-300 ease-in-out ${
                    isExpanded 
                      ? 'max-h-[800px] opacity-100' 
                      : 'max-h-0 opacity-0'
                  }`}
                  style={{ overflow: 'hidden' }}
                >
                  <div className="px-4 pb-4">
                    <div className="grid grid-cols-3 gap-2">
                      {timeSlots.map((time) => {
                        const key = `${day}-${time}`;
                        const isSelected = !!availability[key];
                        const heatmapCount = getHeatmapIntensity(day, time);
                        const heatmapClass = getHeatmapClass(heatmapCount);
                        const tooltip = getHeatmapTooltip(day, time);
                        
                        return (
                          <button
                            key={key}
                            type="button"
                            className={`p-2 rounded-lg text-xs font-medium transition-all duration-200 min-h-[44px] flex items-center justify-center relative
                              ${isSelected 
                                ? 'bg-[#7e55f6] text-white shadow-lg' 
                                : `${heatmapClass} text-gray-300 hover:bg-[rgba(255,255,255,0.1)]`
                              }`
                            }
                            onClick={() => handleMouseDown(day, time, isSelected)}
                            title={tooltip || `${day} at ${time}`}
                          >
                            {time}
                            {/* Small indicator for heatmap data when not selected */}
                            {!isSelected && heatmapCount > 0 && (
                              <div className="absolute top-1 right-1 w-1 h-1 bg-blue-400 rounded-full"></div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
  
  // Desktop view - two cells per row (00 and 30 minutes)
  return (
    <div className="space-y-4">
      <QuickSelectButtons />
      
      <div 
        className="w-full no-select" 
        style={{ userSelect: 'none' }}
        onMouseLeave={handleDragEnd}
        onMouseUp={handleDragEnd}
      >
        <div className="w-full overflow-x-auto">
          <table className="w-full border-collapse bg-black min-w-[820px]">
            <thead>
              <tr>
                <th className="border border-gray-700 p-2 w-20 sm:w-24"></th>
                {daysShort.map((day, index) => (
                  <th 
                    key={day} 
                    colSpan={2}
                    className="border border-gray-700 p-2 text-center text-gray-300 text-sm sm:text-base"
                  >
                    {day}
                  </th>
                ))}
              </tr>
              <tr>
                <th className="border border-gray-700 p-1 text-xs text-gray-400"></th>
                {daysShort.flatMap((day, index) => [
                  <th key={`${day}-00`} className="border border-gray-700 p-1 text-center text-xs text-gray-400">:00</th>,
                  <th key={`${day}-30`} className="border border-gray-700 p-1 text-center text-xs text-gray-400">:30</th>
                ])}
              </tr>
            </thead>
            <tbody>
              {hourGroups.map((hourGroup, hourIndex) => {
                const isEvenRow = hourIndex % 2 === 0;
                const bgClass = isEvenRow ? 'bg-gray-900 bg-opacity-20' : 'bg-black';
                
                return (
                  <tr key={hourGroup.hourLabel} className={bgClass}>
                    {/* Hour label */}
                    <td className="border border-gray-700 p-2 font-medium text-white text-xs sm:text-sm">
                      {hourGroup.hourLabel}
                    </td>
                    
                    {/* Two cells per day (00 and 30 minutes) */}
                    {days.flatMap((day) => {
                      const slot00 = hourGroup.slots[0]; // :00 minute slot
                      const slot30 = hourGroup.slots[1]; // :30 minute slot
                      
                      return [
                        /* :00 minute cell */
                        <td key={`${day}-00`} className="border border-gray-700 p-1 text-center">
                          {slot00 && (
                            <TimeSlotCell
                              day={day}
                              time={slot00.time}
                              isSelected={!!availability[`${day}-${slot00.time}`]}
                              onMouseDown={handleMouseDown}
                              onMouseEnter={handleMouseEnter}
                            />
                          )}
                        </td>,
                        
                        /* :30 minute cell */
                        <td key={`${day}-30`} className="border border-gray-700 p-1 text-center">
                          {slot30 && (
                            <TimeSlotCell
                              day={day}
                              time={slot30.time}
                              isSelected={!!availability[`${day}-${slot30.time}`]}
                              onMouseDown={handleMouseDown}
                              onMouseEnter={handleMouseEnter}
                            />
                          )}
                        </td>
                      ];
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AvailabilityGrid;